import React from 'react';
import { Box, Divider, Drawer, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { FunnelInsightNodeMetric, PaywallOutcomesData, ScreenAnswersData } from '../types';
import { ANALYTICS_CANVAS_TEST_IDS } from '../testIds';
import { StatTierChip } from './StatTierChip';
import { formatPct } from '../statTierCopy';
import { AudienceProvenanceBucketList, AudienceProvenanceSettingsButton } from './AudienceProvenanceBuckets';
import { useAudienceProvenanceSettings } from '../audienceProvenance';
import { PEOPLE_ABSENCE_LABEL, unitNoun, useFunnelUnit } from '../funnelUnit';
import { nodeFlowForUnit, type UnitNodeFlowView } from '../unitMetrics';

/**
 * Spec D D1b: per-NODE drilldown on the analytics canvas (mirrors the
 * FunnelInsightsEdgeDrawer idiom). v1 is deliberately minimal — the node's
 * server-computed metrics + CI/method detail; trends/distributions are v2.
 * Node click on the read-only canvas opens THIS, never the screen editor
 * (AC-D1b-1).
 */

const HEAT_LABEL: Record<string, string> = {
  low: 'low vs funnel median',
  medium: 'medium vs funnel median',
  high: 'high vs funnel median — the leak',
};

interface AnalyticsNodeDrawerProps {
  metric: FunnelInsightNodeMetric | null;
  screenName?: string;
  screenType?: string;
  windowDays?: number;
  /**
   * FU part F (D-F3): this screen's slice of the BATCH screen-answers payload
   * (read synchronously — the drawer never fetches). `undefined`/`null` with
   * answersAvailable=true means the screen simply has no answer data.
   */
  answers?: ScreenAnswersData | null;
  /** false = the batch endpoint failed — show a degraded note, never blank. */
  answersAvailable?: boolean;
  /** P4: paywall product id → display name, resolved from the screen config. */
  productLabels?: Record<string, string>;
  open: boolean;
  onClose: () => void;
}

export const AnalyticsNodeDrawer: React.FC<AnalyticsNodeDrawerProps> = ({
  metric,
  screenName,
  screenType,
  windowDays,
  answers,
  answersAvailable = true,
  productLabels,
  open,
  onClose,
}) => {
  // F2 (FT-AC-090): every flow figure below comes from the page unit's field
  // set; people mode without the person twins renders the D7 typed absence.
  const unit = useFunnelUnit();
  const noun = unitNoun(unit);
  const flow = metric ? nodeFlowForUnit(metric, unit) : null;
  return (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: { width: 340, p: 2 },
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
      <Typography variant="h6">{screenName ?? 'Screen metrics'}</Typography>
      <IconButton
        aria-label="close node details"
        data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerClose}
        onClick={onClose}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Stack>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
      {[screenType, windowDays ? `${windowDays}-day window` : undefined, 'read-only']
        .filter(Boolean)
        .join(' · ')}
    </Typography>

    {metric && flow && (
      <Stack spacing={1.5} data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawer}>
        {/* D5: the unit noun is part of the visible label on every flow row. */}
        <MetricRow
          label={`Visitors · ${noun}`}
          value={flow.available
            ? (flow.visitors ?? 0).toLocaleString('en-US')
            : PEOPLE_ABSENCE_LABEL}
        />
        {/* Pre-answer zone: a −0% here is survivor bias (the filtered cohort
            answered LATER by construction) — caption instead of fake health. */}
        {metric.filterObservability === 'pre' ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
            data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerObservability}
          >
            before the answer — drop-off is not observable for the filtered cohort
          </Typography>
        ) : flow.available ? (
          <DropOffRow flow={flow} noun={noun} />
        ) : null}
        {flow.available && flow.cumulativeReach !== undefined && (
          <MetricRow label={`Cumulative reach · ${noun}`} value={`${formatPct(flow.cumulativeReach)} of entrants`} />
        )}
        {flow.available && flow.qualification && (
          <>
            <MetricRow
              label="95% CI (continue)"
              value={`${formatPct(flow.qualification.ciLow)} – ${formatPct(flow.qualification.ciHigh)}`}
            />
            <MetricRow label="Method" value="Wilson score interval" />
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">Qualification</Typography>
              <StatTierChip qualification={flow.qualification} />
            </Stack>
          </>
        )}
        {metric.money && (
          <>
            <Divider />
            <Typography variant="overline" color="text.secondary">Money</Typography>
            {unit === 'people' && (
              // D8 (FT-AC-087): money stays journey-grain in F2 — said visibly.
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: -1 }}>
                money counts visits
              </Typography>
            )}
            <MetricRow label="Buy clicks" value={metric.money.buyButtonClicks.toLocaleString('en-US')} />
            <MetricRow label="Trials" value={metric.money.trialStartedJourneys.toLocaleString('en-US')} />
            <MetricRow label="Purchases" value={metric.money.purchaseJourneys.toLocaleString('en-US')} />
            <MetricRow label="Offers converted" value={metric.money.offerConvertedJourneys.toLocaleString('en-US')} />
            <MetricRow label="Revenue" value={`$${metric.money.purchaseAmount.toLocaleString('en-US')}`} />
            {metric.money.purchaseConversion && (
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">Purchase conv</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatPct(metric.money.purchaseConversion.p)}
                  </Typography>
                  <StatTierChip qualification={metric.money.purchaseConversion} />
                </Stack>
              </Stack>
            )}
          </>
        )}
        {/* Part B (PB-AC-004): presence-based — renders wherever the payload
            carries outcomes (incl. WebEmbed paywalls the money block skips);
            NEVER gate this on screenType or metric.money. */}
        <PaywallOutcomesSection
          outcomes={answersAvailable ? answers?.paywallOutcomes : undefined}
          productLabels={productLabels}
        />
        <Divider />
        {/* I6 (analytics-versions-and-publish-flow): the journeys↔people
            bridge — say out loud that «entered» counts journeys while
            «answered» counts distinct people. Degrades away whenever the
            answers payload is absent/failed (old cache, endpoint down). */}
        {answersAvailable && answers && answers.answers.length > 0 && (
          <AnswersBridge
            respondents={answers.totalRespondents}
            // FT-AC-091: in people mode with the person twin served, «entered»
            // counts distinct PEOPLE — same unit as «answered», so the
            // mixed-unit disclaimer retires. Any other pairing (visits mode,
            // or people mode on an absent twin) keeps the visits count and
            // the disclaimer verbatim.
            visitors={unit === 'people' && flow.available && typeof metric.visitorsPeople === 'number'
              ? metric.visitorsPeople
              : metric.visitors}
            sameUnit={unit === 'people' && flow.available && typeof metric.visitorsPeople === 'number'}
          />
        )}
        <AnswersSection
          answers={answers}
          answersAvailable={answersAvailable}
          hideAnswersHeader={Boolean(answersAvailable && answers && answers.answers.length > 0)}
        />
        <AudienceSection
          audience={answersAvailable ? answers?.audience : undefined}
          sameUnitVisitors={unit === 'people' && flow.available}
        />
        <Box sx={{ pt: 1, color: 'text.secondary', fontSize: 12, wordBreak: 'break-all' }}>
          {metric.screenId}
        </Box>
      </Stack>
    )}
  </Drawer>
  );
};

/**
 * FU part F (D-F3): user answer distribution (option picks) + per-variable
 * latest-value distribution for the selected screen. Data source is the
 * privacy-filtered variable_updates mart — excluded variables never arrive.
 */
/**
 * I6: journeys↔people honesty bridge above the answers list. «Entered» counts
 * journeys (visits, `metric.visitors`); «answered» counts distinct PEOPLE
 * (`answers.totalRespondents`) — two different units, said out loud. The
 * no-answer delta clamps at 0 (a repeat visitor answers once, so respondents
 * can legitimately exceed journeys on healthy multi-journey data).
 */
function AnswersBridge({
  respondents,
  visitors,
  sameUnit = false,
}: {
  respondents: number;
  visitors: number;
  /**
   * FT-AC-091: true ⇔ «entered» is the person twin (people mode, twin
   * served) — both compared fields are person-grain, so the mixed-unit
   * disclaimer retires. false keeps the visits pairing and the disclaimer
   * verbatim (the negative case: forcing a mixed pair brings it back).
   */
  sameUnit?: boolean;
}): React.ReactElement {
  const noAnswer = Math.max(0, visitors - respondents);
  const pctLine = visitors > 0
    ? ` ${formatPct(respondents / visitors)} = people who answered ÷ ${sameUnit ? 'people who entered' : 'journeys that entered'}.`
    : '';
  return (
    <Stack spacing={0.25} data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAnswersBridge}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="overline" color="text.secondary">
          {`User answers · ${respondents.toLocaleString('en-US')} of ${visitors.toLocaleString('en-US')} entered${sameUnit ? ' · people' : ''}`}
        </Typography>
        <Tooltip
          title={sameUnit
            ? `«Entered» and «answered» both count distinct people.${pctLine}`
            : `«Entered» counts journeys (visits); «answered» counts distinct people — different units.${pctLine} A repeat visitor answers once, so the share can exceed 100% on healthy data.`}
        >
          <InfoOutlinedIcon
            data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAnswersBridgeTooltip}
            sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
          />
        </Tooltip>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAnswersNoAnswer}
      >
        {`${respondents.toLocaleString('en-US')} people answered · ${noAnswer.toLocaleString('en-US')} entered without answering`}
      </Typography>
    </Stack>
  );
}

function AnswersSection({
  answers,
  answersAvailable,
  hideAnswersHeader = false,
}: {
  answers?: ScreenAnswersData | null;
  answersAvailable: boolean;
  /** I6: the bridge above already IS the answers header — skip the local one. */
  hideAnswersHeader?: boolean;
}): React.ReactElement {
  if (!answersAvailable) {
    return (
      <Typography variant="caption" color="text.secondary" data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAnswers}>
        Answer data unavailable for this window.
      </Typography>
    );
  }
  const hasAnswers = Boolean(answers && answers.answers.length > 0);
  const hasVariables = Boolean(answers && answers.variables.length > 0);
  if (!hasAnswers && !hasVariables) {
    return (
      <Typography variant="caption" color="text.secondary" data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAnswers}>
        No answer data for this window.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {hasAnswers && (
        <Stack spacing={0.75} data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAnswers}>
          {!hideAnswersHeader && (
            <Typography variant="overline" color="text.secondary">
              {`User answers (${answers!.totalRespondents.toLocaleString('en-US')})`}
            </Typography>
          )}
          {answers!.answers.map(option => (
            <ShareRow
              key={option.optionId ?? option.label}
              label={option.label}
              users={option.users}
              share={option.share}
            />
          ))}
        </Stack>
      )}
      {answers!.variables.map(variable => (
        <Stack key={variable.variableKey} spacing={0.75} data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerVariable}>
          <Typography variant="overline" color="text.secondary">
            {`Variable: ${variable.variableKey}`}
          </Typography>
          {variable.values.map(entry => (
            <ShareRow key={entry.value} label={entry.value} users={entry.users} share={entry.share} />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * D-5: below this many people a row is still SHOWN (the user explicitly wants
 * non-significant patterns) but carries a muted «~ direction only» caption —
 * same tone as the StatTierChip direction tier.
 */
const AUDIENCE_DIRECTION_ONLY_MAX_N = 30;

/** D-3: UI shows the top combinations; the rest folds into an «other» residual. */
const AUDIENCE_COMBOS_SHOWN = 10;

/**
 * Screen Audience Profile (D-1…D-7): who reached this screen — per-variable
 * distributions of the reachers' LATEST values set ANYWHERE in the funnel,
 * plus top value-combinations. Renders nothing (D-6 — absence, never fake
 * zeros) on old cache payloads, 0 reached people, or an empty profile.
 */
function AudienceSection({
  audience,
  sameUnitVisitors = false,
}: {
  audience?: ScreenAnswersData['audience'];
  /**
   * FT-AC-091: true ⇔ the drawer's Visitors row is person-grain (people mode,
   * twin served) — the «Visitors above counts journeys» disclaimer retires
   * only then; every other pairing keeps it verbatim.
   */
  sameUnitVisitors?: boolean;
}): React.ReactElement | null {
  const { settings, setBucketShown } = useAudienceProvenanceSettings();
  if (!audience || audience.reachedPeople <= 0) return null;
  const hasVariables = audience.variables.length > 0;
  const hasCombos = audience.combos.length > 0;
  // buckets alone must keep the section alive: a screen whose reachers only
  // answered in other visits still has a story to disclose (PA-AC-003)
  const hasBuckets = Boolean(audience.buckets?.after || audience.buckets?.other);
  if (!hasVariables && !hasCombos && !hasBuckets) return null;

  const reached = audience.reachedPeople;
  const shownCombos = audience.combos.slice(0, AUDIENCE_COMBOS_SHOWN);
  const shownComboUsers = shownCombos.reduce((sum, combo) => sum + combo.users, 0);
  // «other» covers the REST of the reachers: truncated combos + people with no profile
  const otherUsers = Math.max(0, reached - shownComboUsers);

  return (
    <>
      <Divider />
      <Stack spacing={1.5} data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAudience}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="overline" color="text.secondary">
            {`Who reached this screen · ${reached.toLocaleString('en-US')} people`}
          </Typography>
          <Tooltip
            title={`People (distinct) who opened this screen in the window. The list shows each person's latest answer given in the SAME visit, on the way to this screen — answers from screens ahead and from earlier visits are counted separately below; expand them for the back-navigation or returnee story.${sameUnitVisitors ? '' : ' Visitors above counts journeys — a person can have several.'}`}
          >
            <InfoOutlinedIcon
              data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAudienceTooltip}
              sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
            />
          </Tooltip>
          {hasBuckets && (
            <AudienceProvenanceSettingsButton settings={settings} onToggle={setBucketShown} />
          )}
        </Stack>
        {audience.variables.map(variable => (
          <Stack
            key={variable.variableKey}
            spacing={0.75}
            data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAudienceVariable}
          >
            <Typography variant="overline" color="text.secondary">{variable.variableKey}</Typography>
            {variable.values.map(entry => (
              <ShareRow
                key={entry.value}
                label={entry.value}
                users={entry.users}
                share={entry.share}
                directionOnly={entry.users < AUDIENCE_DIRECTION_ONLY_MAX_N}
              />
            ))}
            {variable.noValueUsers > 0 && (
              <ShareRow
                label="no value recorded"
                users={variable.noValueUsers}
                share={reached > 0 ? variable.noValueUsers / reached : 0}
                residual
              />
            )}
          </Stack>
        ))}
        <AudienceProvenanceBucketList
          buckets={audience.buckets}
          settings={settings}
          onToggle={setBucketShown}
          afterCaption="answers given later in the same visit — after passing this screen"
          otherCaption="answers given on earlier passes through the funnel"
        />
        {hasCombos && (
          <Stack spacing={0.5} data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAudienceCombos}>
            <Typography variant="overline" color="text.secondary">Top combinations</Typography>
            {shownCombos.map((combo, index) => (
              <ComboRow
                key={combo.pairs.map(pair => `${pair.variableKey}=${pair.value}`).join('|')}
                index={index}
                label={combo.pairs.map(pair => `${pair.variableKey}: ${pair.value}`).join(' · ')}
                users={combo.users}
                share={combo.share}
                directionOnly={combo.users < AUDIENCE_DIRECTION_ONLY_MAX_N}
              />
            ))}
            {otherUsers > 0 && (
              <ComboRow
                index={shownCombos.length}
                label="other"
                users={otherUsers}
                share={reached > 0 ? otherUsers / reached : 0}
                residual
              />
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
}

function ComboRow({
  index,
  label,
  users,
  share,
  directionOnly = false,
  residual = false,
}: {
  index: number;
  label: string;
  users: number;
  share: number;
  directionOnly?: boolean;
  residual?: boolean;
}): React.ReactElement {
  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="space-between"
      alignItems="baseline"
      data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerAudienceCombo(index)}
    >
      <Typography
        variant="body2"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...(residual && { color: 'text.secondary', fontStyle: 'italic' }),
        }}
      >
        {label}
        {directionOnly && <DirectionOnlyCaption />}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {`${users.toLocaleString('en-US')} · ${formatPct(share)}`}
      </Typography>
    </Stack>
  );
}

function DirectionOnlyCaption(): React.ReactElement {
  return (
    <Typography
      component="span"
      variant="caption"
      color="text.secondary"
      sx={{ fontStyle: 'italic', ml: 0.5 }}
    >
      ~ direction only
    </Typography>
  );
}

function ShareRow({
  label,
  users,
  share,
  directionOnly = false,
  residual = false,
  barColor = 'primary',
}: {
  label: string;
  users: number;
  share: number;
  directionOnly?: boolean;
  residual?: boolean;
  barColor?: 'primary' | 'error' | 'success';
}): React.ReactElement {
  return (
    <Box>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
        <Typography
          variant="body2"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...(residual && { color: 'text.secondary', fontStyle: 'italic' }),
          }}
        >
          {label}
          {directionOnly && <DirectionOnlyCaption />}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {`${users.toLocaleString('en-US')} · ${formatPct(share)}`}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, share * 100))}
        color={residual ? 'inherit' : barColor}
        sx={{ height: 4, borderRadius: 1, ...(residual && { opacity: 0.45 }) }}
      />
    </Box>
  );
}

/**
 * Part B — paywall outcome breakdown (plan 2026-07-22, approved mock
 * paywall-outcomes.html): a mutually exclusive partition of openers (rows sum
 * to openers; zero rows are ABSENT — D-6), the never-touched and closed-app
 * captions, extras and the purchases-by-plan mini-list. PEOPLE-based — the
 * Money block above counts journeys; both units stated. Renders ONLY when the
 * payload exists with openers > 0 (PB-AC-004 — presence-based, so WebEmbed
 * paywalls the money block skips still get outcomes).
 */
const PAYWALL_OUTCOME_ROWS: Array<{
  key: keyof PaywallOutcomesData['outcomes'];
  label: string;
  caption?: string;
  success?: boolean;
}> = [
  { key: 'purchased', label: 'Subscribed', success: true },
  {
    key: 'tappedFormClosed',
    label: 'Tapped subscribe → closed the payment form',
    caption: 'payment widget closed, hosted checkout or wallet sheet cancelled',
  },
  { key: 'tappedExplicit', label: 'Tapped subscribe → left the funnel' },
  {
    key: 'tappedSilent',
    label: 'Tapped subscribe → left silently',
    caption: 'incl. abandoned redirect checkout — no cancel event exists there',
  },
  { key: 'movedOn', label: 'Moved on without subscribing' },
  { key: 'viewedExplicit', label: 'Viewed only → left the funnel' },
  { key: 'viewedSilent', label: 'Viewed only → left silently' },
];

const formatDwellSeconds = (ms: number): string => `${Math.round(ms / 1000)} s`;
const shortProductId = (id: string): string => (id.length > 10 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id);

const PAYWALL_OUTCOMES_TOOLTIP =
  'People-based: each person counted once by their events on this screen; % of the openers. ' +
  'The Money block counts journeys — a person can have several. «Never touched» ignores the ' +
  'automatic plan pre-selection at open — interaction starts at a subscribe tap or an actual ' +
  'plan switch. «Left silently after tapping» includes abandoned hosted checkout — leaving ' +
  'that page emits no cancel event; the «closed the payment form» row is the precise sub-signal ' +
  '(embedded widget, hosted checkout or Apple/Google Pay sheet — wallet events exist only from ' +
  'funnel shells that emit them; earlier windows keep wallet cancellers under «left silently»).';

function PaywallOutcomesSection({
  outcomes,
  productLabels,
}: {
  outcomes?: PaywallOutcomesData;
  /** P4: id → display name from the screen config; absent → shortened ids. */
  productLabels?: Record<string, string>;
}): React.ReactElement | null {
  if (!outcomes || outcomes.openers <= 0) return null;
  const { openers } = outcomes;
  const nonSubscribers = Math.max(0, openers - outcomes.outcomes.purchased);
  const planTotal = outcomes.planMix.reduce((sum, row) => sum + row.buyers, 0);

  const extraLines: string[] = [];
  const medians = [
    outcomes.medianOpenToTapMs !== undefined
      ? `median ${formatDwellSeconds(outcomes.medianOpenToTapMs)} open → tap`
      : undefined,
    outcomes.medianTapToConvertMs !== undefined
      ? `${formatDwellSeconds(outcomes.medianTapToConvertMs)} tap → subscribe`
      : undefined,
  ].filter(Boolean).join(' · ');
  if (medians) extraLines.push(medians);
  const counters = [
    outcomes.planSwitchers > 0
      ? `${outcomes.planSwitchers.toLocaleString('en-US')} switched plans before deciding`
      : undefined,
    outcomes.paymentErrors > 0
      ? `${outcomes.paymentErrors.toLocaleString('en-US')} payment errors`
      : undefined,
    outcomes.repeatViewBuyers > 0
      ? `${outcomes.repeatViewBuyers.toLocaleString('en-US')} buyers came back to buy`
      : undefined,
    outcomes.closedXTotal > 0
      ? `${outcomes.closedXTotal.toLocaleString('en-US')} closed via ✕`
      : undefined,
  ].filter(Boolean).join(' · ');
  if (counters) extraLines.push(counters);

  return (
    <>
      <Divider />
      <Stack spacing={1} data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerPaywallOutcomes}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="overline" color="text.secondary">
            {`Paywall outcomes · ${openers.toLocaleString('en-US')} people opened`}
          </Typography>
          <Tooltip title={PAYWALL_OUTCOMES_TOOLTIP}>
            <InfoOutlinedIcon
              data-testid={ANALYTICS_CANVAS_TEST_IDS.nodeDrawerPaywallOutcomesTooltip}
              sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
            />
          </Tooltip>
        </Stack>
        {/* PB-AC-006: units difference said out loud */}
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: -1 }}>
          people · % of openers — the Money block counts journeys
        </Typography>
        {PAYWALL_OUTCOME_ROWS.map(row => {
          const users = outcomes.outcomes[row.key];
          if (users <= 0) return null;
          return (
            <Box key={row.key} data-testid={ANALYTICS_CANVAS_TEST_IDS.paywallOutcomeRow(row.key)}>
              <ShareRow
                label={row.label}
                users={users}
                share={openers > 0 ? users / openers : 0}
                barColor={row.success ? 'success' : 'error'}
              />
              {row.key === 'purchased' && outcomes.convertedTrial > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {`incl. ${outcomes.convertedTrial.toLocaleString('en-US')} trials`}
                </Typography>
              )}
              {row.caption && (
                <Typography variant="caption" color="text.secondary">{row.caption}</Typography>
              )}
            </Box>
          );
        })}
        {outcomes.noTouch > 0 && nonSubscribers > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={ANALYTICS_CANVAS_TEST_IDS.paywallOutcomeRow('no-touch')}
          >
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {outcomes.noTouch.toLocaleString('en-US')}
            </Box>
            {` of the ${nonSubscribers.toLocaleString('en-US')} non-subscribers never touched anything (${formatPct(outcomes.noTouch / nonSubscribers)})`}
          </Typography>
        )}
        {outcomes.closedAppTotal > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={ANALYTICS_CANVAS_TEST_IDS.paywallOutcomeRow('closed-app')}
          >
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {outcomes.closedAppTotal.toLocaleString('en-US')}
            </Box>
            {` closed the tab/app (${formatPct(outcomes.closedAppTotal / openers)}) — confirmed unload or last event = tab hidden`}
          </Typography>
        )}
        {extraLines.length > 0 && (
          <Box
            data-testid={ANALYTICS_CANVAS_TEST_IDS.paywallOutcomesExtras}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.75 }}
          >
            {extraLines.map(line => (
              <Typography key={line} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {line}
              </Typography>
            ))}
          </Box>
        )}
        {outcomes.planMix.length > 0 && (
          <Stack spacing={0.75} data-testid={ANALYTICS_CANVAS_TEST_IDS.paywallOutcomesPlanMix}>
            <Typography variant="overline" color="text.secondary">
              {`Purchases by plan · ${planTotal.toLocaleString('en-US')} total`}
            </Typography>
            {outcomes.planMix.map(row => (
              <ShareRow
                key={row.productId}
                label={productLabels?.[row.productId] ?? shortProductId(row.productId)}
                users={row.buyers}
                share={planTotal > 0 ? row.buyers / planTotal : 0}
                barColor="success"
              />
            ))}
          </Stack>
        )}
      </Stack>
    </>
  );
}

/**
 * FU: drop-off leads with the TOTAL loss (flow delta — arrived minus left,
 * incl. silent abandonment) and, when they differ, breaks out how many were
 * explicit FunnelExited events. A real count always renders even when the
 * rate isn't statistically readable — «−0» on a leaking screen is dishonest.
 */
function DropOffRow({ flow, noun }: { flow: UnitNodeFlowView; noun: string }): React.ReactElement {
  // F2 (FT-AC-090): the row reads the page unit's field view — explicit vs
  // flow-delta selection happened in nodeFlowForUnit, per grain.
  const explicit = flow.explicitExits ?? 0;
  const totalLoss = flow.lossCount ?? 0;
  const rate = flow.lossRate;
  const heat = flow.heatBucket ? ` · ${HEAT_LABEL[flow.heatBucket]}` : '';

  const value = rate !== undefined
    ? `−${totalLoss.toLocaleString('en-US')} (−${formatPct(rate)})${heat}`
    : totalLoss > 0
      ? `−${totalLoss.toLocaleString('en-US')} — rate not readable yet`
      : '0 — no recorded loss';

  return (
    <>
      <MetricRow label={`Drop-off · ${noun}`} value={value} />
      {totalLoss > explicit && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
          {`${explicit.toLocaleString('en-US')} explicit exits · ${(totalLoss - explicit).toLocaleString('en-US')} left without an exit event`}
        </Typography>
      )}
    </>
  );
}

function MetricRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <Stack direction="row" spacing={1} justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
    </Stack>
  );
}
